import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import useProjectStore from "../../store/projectStore";
import useAuthStore from "../../store/authStore";
import { bladesApi } from "../../api/blades";
import { libraryApi } from "../../api/assemblies";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import LineageChart from "../../components/charts/LineageChart";
import { useLookups } from "../../hooks/useLookups";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import { useChartColors } from "../../chartTheme";
import useCheerStore from "../../store/cheerStore";

function Field({ label, value, unit }) {
  if (value == null) return null;
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">
        {typeof value === "number" ? value.toFixed(value % 1 === 0 ? 0 : 3) : value}
        {unit && <span className="ml-1 text-gray-400 font-normal">{unit}</span>}
      </span>
    </div>
  );
}

export default function SummaryPage() {
  const { LINE_COLORS } = useChartColors();
  const { projectId } = useParams();
  const { activeDesign, setActiveDesign, saveConstructionLines, activeProject, projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const triggerCheer = useCheerStore((s) => s.trigger);
  const { types: mecoTypes, labelFor: labelForMeco } = useLookups("meco_type");
  const { labelFor: labelForMachine } = useLookups("machine_type");
  const [projectOpen, setProjectOpen] = useState(false);
  const [unreleasing, setUnreleasing] = useState(false);
  const [settings, setSettings] = useState({
    name:            activeDesign?.name             ?? "",
    description:     activeDesign?.description      ?? "",
    n_blades:        activeDesign?.n_blades         ?? "",
    n_samples:       activeDesign?.n_samples        ?? 50,
    density_factor:  activeDesign?.density_factor   ?? 0.0,
    topology:         activeDesign?.topology          ?? "RoundEdge",
    thickness_option: activeDesign?.thickness_option ?? "AzimuthalNormal",
    meco_type_id:        activeDesign?.meco_type_id        ?? null,
    spanwise_degree:     activeDesign?.degree_spanwise     ?? 2,
    rotation_direction:  activeDesign?.rotation_direction  ?? "CCW",
  });
  const [saving,       setSaving]      = useState(false);
  const [checkingOut,  setCheckingOut] = useState(false);
  const [checkingIn,   setCheckingIn]  = useState(false);
  const [error,        setError]       = useState(null);

  // Release modal
  const [showRelease,  setShowRelease] = useState(false);
  const [releaseErr,   setReleaseErr]  = useState(null);
  const [releasing,    setReleasing]   = useState(false);

  // Copy modal
  const [showCopy,     setShowCopy]    = useState(false);
  const [copyName,     setCopyName]    = useState("");
  const [copyProjectId, setCopyProjectId] = useState("");
  const [copyErr,      setCopyErr]     = useState(null);
  const [copying,      setCopying]     = useState(false);

  const cls = activeDesign?.construction_lines || [];
  // Stable key of active flags — used as effect dep so toggles re-sync when CL data arrives
  const clinesActiveKey = cls.map(cl => `${cl.name}:${cl.active}`).join('|');
  const [clineToggles, setClineToggles] = useState(() => {
    const out = {};
    cls.forEach(cl => { out[cl.name] = cl.active !== false; });
    return out;
  });

  useEffect(() => {
    if (!activeDesign) return;
    setSettings({
      name:            activeDesign.name             ?? "",
      description:     activeDesign.description      ?? "",
      n_blades:        activeDesign.n_blades         ?? "",
      n_samples:       activeDesign.n_samples        ?? 50,
      density_factor:  activeDesign.density_factor   ?? 0.0,
      topology:         activeDesign.topology          ?? "RoundEdge",
      thickness_option: activeDesign.thickness_option ?? "AzimuthalNormal",
      meco_type_id:        activeDesign.meco_type_id        ?? null,
      spanwise_degree:     activeDesign.degree_spanwise     ?? 2,
      rotation_direction:  activeDesign.rotation_direction  ?? "CCW",
    });
    const out = {};
    (activeDesign.construction_lines || []).forEach(cl => { out[cl.name] = cl.active !== false; });
    setClineToggles(out);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDesign?.id, activeDesign?.updated_at, clinesActiveKey]);

  const activeCount = cls.filter((cl, i) =>
    i === 0 || i === cls.length - 1 || (clineToggles[cl.name] ?? true)
  ).length;
  const maxDegree = Math.max(1, activeCount - 1);

  useEffect(() => {
    setSettings(s => {
      const cur = parseInt(s.spanwise_degree, 10) || 1;
      const clamped = Math.min(Math.max(1, cur), maxDegree);
      return cur === clamped ? s : { ...s, spanwise_degree: clamped };
    });
  }, [maxDegree]);

  const set = (k) => (e) => setSettings(s => ({ ...s, [k]: e.target.value }));

  const saveAll = async () => {
    if (!activeDesign) return;
    if (!settings.name.trim()) { setError("Design name cannot be empty"); return; }
    const n = parseInt(settings.n_blades, 10);
    if (!n || n < 1) { setError("Enter a valid number of blades"); return; }
    const ns = parseInt(settings.n_samples, 10);
    if (!ns || ns < 50 || ns > 200) { setError("Samples must be between 50 and 200"); return; }
    const df = parseFloat(settings.density_factor);
    if (isNaN(df) || df < 0 || df > 1) { setError("LE/TE clustering must be between 0 and 1"); return; }
    setError(null);
    setSaving(true);
    try {
      const sd = Math.min(Math.max(1, parseInt(settings.spanwise_degree, 10) || 1), maxDegree);
      // Save CLs first so the blade update response sees up-to-date active flags
      if (cls.length > 0) {
        await saveConstructionLines(
          activeDesign.id,
          cls.map((cl, i) => ({
            ...cl,
            active: (i === 0 || i === cls.length - 1) ? true : (clineToggles[cl.name] ?? true),
          })),
        );
      }
      const { data } = await bladesApi.update(activeDesign.id, {
        name: settings.name.trim(),
        description: settings.description,
        n_blades: n, n_samples: ns, density_factor: df,
        topology: settings.topology, thickness_option: settings.thickness_option,
        meco_type_id: settings.meco_type_id,
        degree_spanwise: sd,
        rotation_direction: settings.rotation_direction,
      });
      setActiveDesign(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const isReadOnlyForShortcut = !!activeDesign?.is_released ||
    (!!activeDesign?.checked_out_by_id && activeDesign?.checked_out_by_id !== user?.id);
  useSaveShortcut(saveAll, { disabled: saving || isReadOnlyForShortcut });

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;

  const isReleased       = activeDesign.is_released;
  const checkedOutById   = activeDesign.checked_out_by_id;
  const isCheckedOutByMe = checkedOutById === user?.id;
  const isCheckedOut     = !!checkedOutById;
  const isLockedByOther  = isCheckedOut && !isCheckedOutByMe;
  const isReadOnly       = isReleased || isLockedByOther;

  const doCheckout = async () => {
    setError(null);
    setCheckingOut(true);
    try {
      const { data } = await bladesApi.checkout(activeDesign.id);
      setActiveDesign(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  };

  const doCheckin = async () => {
    setError(null);
    setCheckingIn(true);
    try {
      const { data } = await bladesApi.checkin(activeDesign.id);
      setActiveDesign(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Check-in failed");
    } finally {
      setCheckingIn(false);
    }
  };

  const openRelease = () => {
    setReleaseErr(null);
    setShowRelease(true);
  };

  const doRelease = async () => {
    setReleaseErr(null);
    setReleasing(true);
    try {
      const { data } = await bladesApi.release(activeDesign.id);
      setActiveDesign(data);
      setShowRelease(false);
      triggerCheer("release");
    } catch (e) {
      setReleaseErr(e?.response?.data?.error || "Release failed");
    } finally {
      setReleasing(false);
    }
  };

  const openCopy = () => {
    if (!projects.length) fetchProjects();
    setCopyName(`${activeDesign.name} (copy)`);
    setCopyProjectId(String(activeDesign.project_id));
    setCopyErr(null);
    setShowCopy(true);
  };

  const doCopy = async () => {
    setCopyErr(null);
    setCopying(true);
    try {
      await libraryApi.copyDesign(activeDesign.id, parseInt(copyProjectId), copyName);
      setShowCopy(false);
    } catch (e) {
      setCopyErr(e?.response?.data?.error || "Copy failed");
    } finally {
      setCopying(false);
    }
  };

  const doUnrelease = async () => {
    if (!window.confirm("Revert this design to draft? It will become editable again and lose its release name.")) return;
    setError(null);
    setUnreleasing(true);
    try {
      const { data } = await bladesApi.unrelease(activeDesign.id);
      setActiveDesign(data);
      triggerCheer("unrelease");
    } catch (e) {
      setError(e?.response?.data?.error || "Unrelease failed");
    } finally {
      setUnreleasing(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div className="p-6 space-y-6 max-w-4xl">

      {/* ── Project Information (collapsible) ── */}
      {activeProject && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setProjectOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-left">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Project</span>
              <span className="text-sm font-medium text-gray-800">{activeProject.name}</span>
              {activeProject.proj_id && (
                <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                  {activeProject.proj_id}
                </span>
              )}
              {activeProject.erp_id && (
                <span className="text-xs text-gray-400 border border-gray-200 px-2 py-0.5 rounded">
                  {activeProject.erp_id}
                </span>
              )}
              {activeProject.machine_type_id && (
                <span className="text-xs font-medium text-bh-600 bg-bh-50 border border-bh-200 px-2 py-0.5 rounded-full">
                  {labelForMachine(activeProject.machine_type_id)}
                </span>
              )}
            </div>
            {projectOpen
              ? <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
              : <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />}
          </button>

          {projectOpen && (
            <div className="border-t border-gray-100 px-4 pb-4 pt-2 space-y-0">
              {activeProject.description && (
                <Field label="Description" value={activeProject.description} />
              )}
              <Field label="Owner" value={activeProject.owner_full_name || activeProject.owner_username} />
              {user?.is_admin && activeProject.entity && <Field label="Entity" value={activeProject.entity} />}
              {activeProject.erp_id && <Field label="ERP ID" value={activeProject.erp_id} />}
              <Field label="Created" value={fmt(activeProject.created_at)} />
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Project page</span>
                <Link to={`/projects/${projectId}`}
                  className="text-sm font-medium text-bh-600 hover:text-bh-700 hover:underline">
                  {activeProject.proj_id ?? activeProject.name} →
                </Link>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-500">Layout Tool</span>
                <Link to={`/projects/${projectId}/layout`}
                  className="text-sm font-medium text-bh-600 hover:text-bh-700 hover:underline">
                  Open →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Top: info + settings ── */}
      <div className="grid grid-cols-2 gap-6">

        {/* Design Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Design Information</p>
          {/* Name — editable */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500 shrink-0">Name</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.name}</span>
            ) : (
              <input
                type="text"
                value={settings.name}
                onChange={e => setSettings(s => ({ ...s, name: e.target.value }))}
                className="text-sm font-medium text-gray-900 text-right border border-transparent
                  hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                  bg-transparent focus:bg-white w-56 transition-colors"
              />
            )}
          </div>

          <Field label="Version" value={activeDesign.version} />

          {/* Description — editable */}
          <div className="flex items-start justify-between py-2 border-b border-gray-100 gap-4">
            <span className="text-sm text-gray-500 shrink-0 mt-0.5">Description</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900 text-right">{activeDesign.description || "—"}</span>
            ) : (
              <textarea
                value={settings.description}
                onChange={e => setSettings(s => ({ ...s, description: e.target.value }))}
                rows={2}
                placeholder="Optional note…"
                className="text-sm text-gray-900 text-right border border-transparent
                  hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                  bg-transparent focus:bg-white w-56 resize-none transition-colors"
              />
            )}
          </div>
          {activeDesign.hyco_id && <Field label="HYCO" value={activeDesign.hyco_id} />}
          {activeDesign.derived_from?.hyco_id && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Derived from</span>
              <Link
                to={`/projects/${activeDesign.derived_from.project_id}/blade/${activeDesign.derived_from.id}/summary`}
                className="text-sm font-mono font-medium text-bh-600 hover:text-bh-700 hover:underline"
              >
                {activeDesign.derived_from.hyco_id}
              </Link>
            </div>
          )}
          <Field label="Created"        value={fmt(activeDesign.created_at)} />
          {activeDesign.created_by  && <Field label="Created by"    value={activeDesign.created_by} />}
          <Field label="Last edited"    value={fmt(activeDesign.updated_at)} />
          {activeDesign.updated_by  && <Field label="Last edited by" value={activeDesign.updated_by} />}
          {user?.is_admin && activeProject?.entity && <Field label="Entity" value={activeProject.entity} />}
        </div>

        {/* Design Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Design Settings</p>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Component type</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{labelForMeco(activeDesign.meco_type_id)}</span>
            ) : (
              <select value={settings.meco_type_id ?? ""}
                onChange={e => setSettings(s => ({ ...s, meco_type_id: parseInt(e.target.value) || null }))}
                className="text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white">
                <option value="">— Not set —</option>
                {mecoTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Topology</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.topology ?? "—"}</span>
            ) : (
              <select value={settings.topology} onChange={set("topology")}
                className="text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white">
                <option value="RoundEdge">Round Edge</option>
                <option value="DoveTail">Dove Tail</option>
              </select>
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Thickness normal</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">
                {activeDesign.thickness_option === "Normal" ? "Surface Normal" : "Azimuthal Normal"}
              </span>
            ) : (
              <select value={settings.thickness_option} onChange={set("thickness_option")}
                className="text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white">
                <option value="AzimuthalNormal">Azimuthal Normal</option>
                <option value="Normal">Surface Normal</option>
              </select>
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Rotation direction</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">
                {activeDesign.rotation_direction ?? "CCW"}
              </span>
            ) : (
              <select value={settings.rotation_direction} onChange={set("rotation_direction")}
                className="text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white">
                <option value="CCW">CCW (default)</option>
                <option value="CW">CW</option>
              </select>
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">No. of blades</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.n_blades ?? "—"}</span>
            ) : (
              <input type="number" min="1" max="20" value={settings.n_blades} onChange={set("n_blades")}
                className="w-16 text-sm text-right border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500" />
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Meridional samples</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.n_samples ?? 50}</span>
            ) : (
              <input type="number" min="50" max="200" value={settings.n_samples} onChange={set("n_samples")}
                className="w-16 text-sm text-right border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500" />
            )}
          </div>

          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">LE/TE clustering</span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.density_factor ?? 0}</span>
            ) : (
              <input type="number" min="0" max="1" step="0.05" value={settings.density_factor} onChange={set("density_factor")}
                className="w-16 text-sm text-right border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500" />
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-500">
              Spanwise degree
              <span className="ml-1.5 text-xs text-gray-400">(max {maxDegree})</span>
            </span>
            {isReadOnly ? (
              <span className="text-sm font-medium text-gray-900">{activeDesign.degree_spanwise ?? 2}</span>
            ) : (
              <input type="number" min="1" max={maxDegree} value={settings.spanwise_degree}
                onChange={e => setSettings(s => ({
                  ...s,
                  spanwise_degree: Math.min(Math.max(1, parseInt(e.target.value, 10) || 1), maxDegree),
                }))}
                className="w-16 text-sm text-right border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-bh-500" />
            )}
          </div>
        </div>
      </div>

      {/* ── Stream Lines ── */}
      {cls.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Stream Lines</p>
          <div className="flex flex-wrap gap-2">
            {cls.map((cl, i) => {
              const isFixed  = i === 0 || i === cls.length - 1;
              const isActive = isFixed ? true : (clineToggles[cl.name] ?? true);
              return (
                <label key={cl.name}
                  className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border select-none transition-colors
                    ${isFixed
                      ? "border-gray-100 bg-gray-50 text-gray-500 cursor-default"
                      : isActive
                        ? "border-bh-200 bg-bh-50 text-bh-700 cursor-pointer"
                        : "border-gray-200 bg-white text-gray-400 cursor-pointer"}`}>
                  <input type="checkbox" checked={isActive}
                    disabled={isFixed || isReadOnly}
                    onChange={e => !isFixed && !isReadOnly && setClineToggles(t => ({ ...t, [cl.name]: e.target.checked }))}
                    className="rounded" />
                  <span className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#9ca3af" }} />
                  {cl.name}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Checkout Status ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Checkout Status</p>
        {isCheckedOut ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full shrink-0 ${isCheckedOutByMe ? "bg-bh-500" : "bg-amber-500"}`} />
              <span className={`text-sm font-medium ${isCheckedOutByMe ? "text-bh-700" : "text-amber-700"}`}>
                {isCheckedOutByMe ? "Checked out by you" : `Checked out by ${activeDesign.checked_out_by}`}
              </span>
            </div>
            {activeDesign.checked_out_at && (
              <Field label="Checked out at" value={fmt(activeDesign.checked_out_at)} />
            )}
            {!isCheckedOutByMe && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                This design is locked for editing. Only {activeDesign.checked_out_by} can check it in.
              </p>
            )}
            {isCheckedOutByMe && !isReleased && (
              <button onClick={doCheckin} disabled={checkingIn}
                className="mt-1 text-xs text-bh-700 border border-bh-300 bg-bh-50 hover:bg-bh-100 rounded px-3 py-1.5 transition-colors disabled:opacity-50">
                {checkingIn ? "Checking in…" : "Check In"}
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
              <span className="text-sm text-gray-500">Available — not checked out</span>
            </div>
            {!isReleased && (
              <button onClick={doCheckout} disabled={checkingOut}
                className="text-xs text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 rounded px-3 py-1.5 transition-colors disabled:opacity-50">
                {checkingOut ? "Checking out…" : "Check Out"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Release Status ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Release Status</p>
        {isReleased ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                <span className="text-sm font-medium text-green-700">Released</span>
              </div>
              {user?.is_admin && (
                <button
                  onClick={doUnrelease}
                  disabled={unreleasing}
                  className="text-xs text-red-600 border border-red-300 bg-white hover:bg-red-50 rounded px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                  {unreleasing ? "Unreleasing…" : "Unrelease"}
                </button>
              )}
            </div>
            {activeDesign.released_by && <Field label="Released by" value={activeDesign.released_by} />}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
              <span className="text-sm text-gray-500">Not released</span>
            </div>
            {!isLockedByOther && (
              <button onClick={openRelease}
                className="text-xs text-bh-600 border border-bh-300 bg-bh-50 hover:bg-bh-100 rounded px-3 py-1.5 transition-colors">
                Release…
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Provenance ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Provenance</p>
        <LineageChart bladeId={activeDesign.id} />
      </div>

      {/* ── Single Save + Copy ── */}
      <div className="flex items-center justify-between gap-3">
        {!isReadOnly ? (
          <div className="flex items-center gap-3">
            <button onClick={saveAll} disabled={saving}
              className="text-sm bg-bh-600 text-white rounded-lg px-5 py-2 hover:bg-bh-700 disabled:opacity-50 transition-colors font-medium">
              {saving ? "Saving…" : <>{`Save `}<kbd className="text-[10px] font-sans font-normal opacity-55">Ctrl+S</kbd></>}
            </button>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        ) : <div />}
        <button onClick={openCopy}
          className="text-xs text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 rounded px-3 py-1.5 transition-colors">
          Copy design…
        </button>
      </div>

      {/* ── Release modal ── */}
      <Modal open={showRelease} onClose={() => setShowRelease(false)} title="Release design">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This design will become read-only. This action is permanent.
          </p>
          {releaseErr && <p className="text-red-500 text-sm">{releaseErr}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowRelease(false)}
              className="text-sm text-gray-600 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={doRelease} disabled={releasing}
              className="text-sm bg-bh-600 text-white px-4 py-2 rounded-lg hover:bg-bh-700 disabled:opacity-50 transition-colors">
              {releasing ? "Releasing…" : "Confirm release"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Copy modal ── */}
      <Modal open={showCopy} onClose={() => setShowCopy(false)} title="Copy design">
        <div className="space-y-4">
          <Input label="Name" value={copyName} onChange={e => setCopyName(e.target.value)} />
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-1">
              Target project
            </label>
            <select value={copyProjectId} onChange={e => setCopyProjectId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500">
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {copyErr && <p className="text-red-500 text-sm">{copyErr}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowCopy(false)}
              className="text-sm text-gray-600 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={doCopy} disabled={copying || !copyName.trim() || !copyProjectId}
              className="text-sm bg-bh-600 text-white px-4 py-2 rounded-lg hover:bg-bh-700 disabled:opacity-50 transition-colors">
              {copying ? "Copying…" : "Copy here"}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
